// @flow
import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import type { Location, Match } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';
import { dynamicClassName } from 'utils/dynamicClassName';
import CardThumbnail from 'components/fragments/card/CardThumbnail';
import PagePartners from 'containers/page/PagePartnersContainers';
import type { CardType } from 'types/PageList';
import type { BlocContent } from 'types/NewsletterBloc';
import type { AlbumType } from 'types/Albums';
import type { ArticleType } from 'types/Article';
import type { Status } from 'types/Status';

import { STATUS_DEFAULT, STATUS_UNKNOWN, STATUS_LOADING } from 'constants/statusConstants';
import ContentBloc from 'components/newsletter/blocs/ContentBloc';
import Loading from 'components/fragments/Loading';
import Breadcrumb from 'components/fragments/Breadcrumb';
import FooterArticle from 'components/newsletter/FooterArticle';
import PageMedia from 'components/page/template/PageMedia';

export type StateProps = {
  albums: AlbumType[],
  albums_filtres: {
    term_id: number,
    name: string,
    slug: string
  }[],
  albums_saisons: {
    term_id: number,
    name: string,
    slug: string
  }[],
  albumsStatus: Status,
  title: string,
  excerpt: string,
  description: BlocContent[],
  sidebar: BlocContent[],
  label: {
    id: number,
    name: string,
    slug: string
  },
  featured_media: {
    src: string,
    legend: string
  },
  liste_cards: CardType[],
  articles: ArticleType[],
  status: Status,
  template: string,
  meta_description: string
};

export type DispatchProps = {
  fetchPageList: (slug: string) => void,
  fetchAlbums: () => void
};

type Props = {
  location: Location,
  match: Match,
  pageSlug: string
} & DispatchProps &
  StateProps;

class PageList extends Component<Props> {
  componentDidMount() {
    const { slug } = this.props.match.params;
    if (slug) {
      this.props.fetchPageList(slug);
    } else {
      this.props.fetchPageList(this.props.pageSlug);
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    const { slug } = this.props.match.params;
    if (nextProps !== this.props) {
      if (nextProps.match.params.slug !== slug) {
        if (nextProps.match.params.slug) {
          this.props.fetchPageList(nextProps.match.params.slug);
          return true;
        }
      }
      return true;
    }
    return false;
  }

  componentDidUpdate(prevProps, prevState) {
    const { template, fetchAlbums, albumsStatus } = this.props;
    const { template: prevTemplate } = prevProps;
    if (
      (prevTemplate !== template && template === 'mediatheque') ||
      (albumsStatus === STATUS_DEFAULT && template === 'mediatheque')
    ) {
      fetchAlbums();
    }
  }

  renderCards = () => {
    const { slug } = this.props.match.params;
    const { liste_cards } = this.props;
    const restCards = liste_cards.length % 3;
    return liste_cards.map((card, index) => {
      const isOnLastLine =
        (restCards === 0 && index >= liste_cards.length - 3) ||
        (restCards !== 0 && index >= liste_cards.length - restCards);

      const classNameContainer = dynamicClassName('col col-md-6 col-lg-4 mb-2 mb-md-3');
      isOnLastLine ? classNameContainer.add('mb-lg-0') : classNameContainer.add('mb-lg-4');
      return (
        <div key={index} className={classNameContainer.build()}>
          <CardThumbnail
            url={`${slug ? `/${slug}` : ''}/${card.object_slug}`}
            backgroundUrl={card.featured_media}
            title={card.title}
            desc={card.description}
            linkLabel={card.texte_cta}
          />
        </div>
      );
    });
  };

  renderDescription = () => {
    const { description } = this.props;
    return description.map((blocFigure, index) => <ContentBloc key={index} {...blocFigure} />);
  };

  render() {
    const { title, featured_media, label, status, template } = this.props;
    if (status === STATUS_LOADING) {
      return <Loading />;
    }

    if (status === STATUS_UNKNOWN) {
      return <Redirect to="404" />;
    }

    if (template === 'partenaires') {
      return <PagePartners {...this.props} />;
    }

    if (template === 'mediatheque') {
      return <PageMedia {...this.props} />;
    }

    return (
      <>
        <section className="banner banner--big">
          <div className="banner__thumbnail img-blue-alt" style={{ backgroundImage: `url(${featured_media.src})` }} />
          <div className="container">
            <Breadcrumb elements={[{ name: 'accueil', to: '/' }, title]} isDark />
            <h1 className="ft-h2">{title}</h1>
            <p className="banner__desc">{this.renderDescription()}</p>
          </div>
        </section>
        <div className="container">
          <div className="row mb-6 mb-md-7 mb-lg-8">{this.renderCards()}</div>
          <FooterArticle articles={this.props.articles} slug={label.slug} label={label.name} />
        </div>
      </>
    );
  }
}

export default withHelmet(PageList)((Helmet, pageProps) => {
  const ogimage = pageProps.featured_media.src;
  const ogimagetitle = pageProps.featured_media.title;
  return (
    <Helmet>
      <title>{pageProps.meta_title}</title>
      <meta name={'description'} content={pageProps.meta_description} />
      <link rel={'canonical'} href={window.location.href} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={pageProps.meta_title} />
      <meta property={'og:description'} content={pageProps.meta_description} />
      {ogimage && <meta property={'og:image'} content={ogimage} />}
      {ogimage && ogimagetitle && <meta property={'og:image:title'} content={ogimagetitle} />}
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
